import { signalStoreFeature, watchState, withHooks, patchState } from '@ngrx/signals';

/**
 * Persists the store state in localStorage under `key` and restores it on init.
 * Pass `persistedKeys` to only persist part of the state; everything else starts from its initial value.
 */
export function withHydration<T extends object>(
  key: string,
  defaultState: T,
  persistedKeys?: (keyof T)[]
) {
  const pick = (state: T): Partial<T> =>
    persistedKeys
      ? (Object.fromEntries(
          persistedKeys.filter((k) => k in state).map((k) => [k, state[k]])
        ) as Partial<T>)
      : state;

  return signalStoreFeature(
    withHooks({
      onInit(store) {
        const saved = localStorage.getItem(key);

        patchState(store, (state) => {
          try {
            return saved ? { ...state, ...pick(JSON.parse(saved)) } : defaultState;
          } catch {
            return defaultState;
          }
        });

        watchState(store, (state) => {
          localStorage.setItem(key, JSON.stringify(pick(state as T)));
        });
      },
    })
  );
}
